import { useState } from 'react';
import { Character, CHARACTER_CLASSES } from '@gamedate/core-character/character';
import './CharacterSelectionModal.css';

interface CharacterSelectionModalProps {
  characters: Character[];
  onSelect: (character: Character) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CharacterSelectionModal({
  characters,
  onSelect,
  onCancel,
  isLoading = false
}: CharacterSelectionModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(
    characters.find(c => c.isActive)?.id || characters[0]?.id || null
  );

  const getClassIcon = (characterClass: string) => {
    const classInfo = CHARACTER_CLASSES.find(c => c.value === characterClass);
    return classInfo?.icon || '\uD83D\uDDE1\uFE0F';
  };

  const getGenderIcon = (gender: string) => {
    return gender === 'female' ? '\uD83D\uDC69' : '\uD83D\uDC68';
  };

  const handleConfirm = () => {
    const selected = characters.find(c => c.id === selectedId);
    if (selected) {
      onSelect(selected);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="character-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Character</h2>
          <button className="close-btn" onClick={onCancel} disabled={isLoading}>
            ×
          </button>
        </div>

        <p className="modal-description">
          Choose which character to bring into the room
        </p>

        <div className="character-options">
          {characters.map((character) => (
            <div
              key={character.id}
              className={`character-option ${selectedId === character.id ? 'selected' : ''}`}
              onClick={() => !isLoading && setSelectedId(character.id)}
            >
              <div className="character-avatar-large">
                <span className="class-icon">{getClassIcon(character.characterClass)}</span>
                <span className="gender-badge">{getGenderIcon(character.gender)}</span>
              </div>
              <div className="character-details">
                <div className="character-name">{character.name}</div>
                <div className="character-meta">
                  <span className="character-class">{character.characterClass}</span>
                  <span className="character-level">Level {character.level}</span>
                </div>
              </div>
              {character.isActive && (
                <span className="active-indicator">Active</span>
              )}
              {selectedId === character.id && (
                <span className="check-mark">✓</span>
              )}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="confirm-btn"
            onClick={handleConfirm}
            disabled={!selectedId || isLoading}
          >
            {isLoading ? 'Joining...' : 'Join with Character'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CharacterSelectionModal;
